/**
 * Design System Tokens
 *
 * This file contains the design system foundation including colors, typography,
 * spacing, elevation, and border radius values used throughout the application.
 *
 * Requirements: 1.1, 1.2, 1.3, 1.4, 1.5
 */

export const designTokens = {
  // Color Palette
  colors: {
    primary: {
      main: "#2563eb",
      light: "#42a5f5",
      dark: "#1565c0",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#526176",
      light: "#718198",
      dark: "#17253b",
      contrastText: "#ffffff",
    },
    success: {
      main: "#2e7d32",
      light: "#4caf50",
      dark: "#1b5e20",
      contrastText: "#ffffff",
    },
    warning: {
      main: "#8a5800",
      light: "#ff9800",
      dark: "#654100",
      contrastText: "#ffffff",
    },
    error: {
      main: "#d32f2f",
      light: "#ef5350",
      dark: "#c62828",
      contrastText: "#ffffff",
    },
    info: {
      main: "#0288d1",
      light: "#03a9f4",
      dark: "#01579b",
      contrastText: "#ffffff",
    },
    grey: {
      50: "#fafafa",
      100: "#f5f5f5",
      200: "#eeeeee",
      300: "#e0e0e0",
      400: "#bdbdbd",
      500: "#9e9e9e",
      600: "#757575",
      700: "#616161",
      800: "#424242",
      900: "#212121",
    },
  },

  // Typography Scale
  typography: {
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
    h4: {
      fontSize: "34px",
      fontWeight: 600,
      lineHeight: 1.235,
      letterSpacing: "0.00735em",
    },
    h5: {
      fontSize: "24px",
      fontWeight: 600,
      lineHeight: 1.334,
      letterSpacing: "0em",
    },
    h6: {
      fontSize: "20px",
      fontWeight: 600,
      lineHeight: 1.6,
      letterSpacing: "0.0075em",
    },
    body1: {
      fontSize: "16px",
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: "0.00938em",
    },
    body2: {
      fontSize: "14px",
      fontWeight: 400,
      lineHeight: 1.43,
      letterSpacing: "0.01071em",
    },
    caption: {
      fontSize: "12px",
      fontWeight: 400,
      lineHeight: 1.66,
      letterSpacing: "0.03333em",
    },
    button: {
      fontSize: "14px",
      fontWeight: 500,
      lineHeight: 1.75,
      letterSpacing: "0.02857em",
      textTransform: "none",
    },
  },

  // Spacing System (8px grid)
  spacing: {
    xs: "4px",
    sm: "8px",
    md: "16px",
    lg: "24px",
    xl: "32px",
    xxl: "48px",
    xxxl: "64px",
  },

  // Elevation (Box Shadows)
  elevation: {
    0: "none",
    1: "0px 2px 4px rgba(0, 0, 0, 0.1)",
    2: "0px 4px 8px rgba(0, 0, 0, 0.12)",
    3: "0px 8px 16px rgba(0, 0, 0, 0.14)",
    4: "0px 12px 24px rgba(0, 0, 0, 0.16)",
    8: "0px 16px 32px rgba(0, 0, 0, 0.18)",
  },

  // Border Radius
  borderRadius: {
    small: "4px",
    medium: "8px",
    large: "12px",
    round: "50%",
  },

  // Breakpoints (Requirements 9.1, 9.2, 9.3, 9.4)
  breakpoints: {
    xs: 0, // Mobile: 0-599px
    sm: 600, // Tablet: 600-959px
    md: 960, // Desktop: 960-1279px
    lg: 1280, // Large Desktop: 1280-1919px
    xl: 1920, // Extra Large: 1920px+
  },

  // Touch Target Sizes (Requirement 9.6)
  touchTargets: {
    minimum: 44, // Minimum touch target size for mobile (44x44px)
    comfortable: 48, // Comfortable touch target size
    large: 56, // Large touch target size
  },

  // Responsive Typography (Requirement 9.5)
  responsiveTypography: {
    h4: {
      mobile: "28px", // Smaller on mobile for readability
      tablet: "32px",
      desktop: "34px",
    },
    h5: {
      mobile: "20px",
      tablet: "22px",
      desktop: "24px",
    },
    h6: {
      mobile: "18px",
      tablet: "19px",
      desktop: "20px",
    },
    body1: {
      mobile: "14px", // Slightly smaller on mobile
      tablet: "15px",
      desktop: "16px",
    },
    body2: {
      mobile: "13px",
      tablet: "13px",
      desktop: "14px",
    },
    button: {
      mobile: "14px",
      tablet: "14px",
      desktop: "14px",
    },
  },

  // Transitions (Requirements 1.6, 11.4, 11.5)
  transitions: {
    duration: {
      shortest: 150,
      shorter: 200,
      short: 250,
      standard: 300,
      complex: 375,
      enteringScreen: 225,
      leavingScreen: 195,
    },
    easing: {
      easeInOut: "cubic-bezier(0.4, 0, 0.2, 1)",
      easeOut: "cubic-bezier(0.0, 0, 0.2, 1)",
      easeIn: "cubic-bezier(0.4, 0, 1, 1)",
      sharp: "cubic-bezier(0.4, 0, 0.6, 1)",
    },
    // Simplified transition strings for common use
    fast: "150ms cubic-bezier(0.4, 0, 0.2, 1)",
    normal: "250ms cubic-bezier(0.4, 0, 0.2, 1)",
    slow: "350ms cubic-bezier(0.4, 0, 0.2, 1)",
  },

  // Z-Index Scale
  zIndex: {
    mobileStepper: 1000,
    speedDial: 1050,
    appBar: 1100,
    drawer: 1200,
    modal: 1300,
    snackbar: 1400,
    tooltip: 1500,
  },
};

export default designTokens;
